import React, { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Header right-slot infrastructure.
 *
 * Any page that wants to contribute buttons, filters, menus, or anything else to
 * the right side of the dashboard header just wraps its JSX in <HeaderActions>.
 * The header itself has no knowledge of what any particular page puts there.
 *
 *   <HeaderActionsSlotProvider>    ← mount once (DashboardLayout)
 *     ...
 *     <HeaderActionsSlot />        ← the portal target (DashboardHeader)
 *     ...
 *     <HeaderActions>              ← used by any page / router
 *       <MyFilter /> <MyButton /> <DashboardMenu />
 *     </HeaderActions>
 *   </HeaderActionsSlotProvider>
 */

const HeaderActionsContext = createContext(null);

export const HeaderActionsSlotProvider = ({ children }) => {
    const [slotNode, setSlotNode] = useState(null);
    return (
        <HeaderActionsContext.Provider value={{ slotNode, setSlotNode }}>
            {children}
        </HeaderActionsContext.Provider>
    );
};

// The DOM target the header reserves. Pages' <HeaderActions> portal into this node.
export const HeaderActionsSlot = ({ className, ...rest }) => {
    const ctx = useContext(HeaderActionsContext);
    if (!ctx) {
        // Render a plain div so the header layout still works even if the
        // provider is missing (e.g. during isolated component tests).
        return <div className={className} {...rest} />;
    }
    return <div ref={ctx.setSlotNode} className={className} {...rest} />;
};

// Used by pages (or a route-level router) to inject content into the slot.
// Renders nothing when the slot isn't mounted yet (e.g. first frame on login).
export const HeaderActions = ({ children }) => {
    const ctx = useContext(HeaderActionsContext);
    if (!ctx || !ctx.slotNode) return null;
    return createPortal(children, ctx.slotNode);
};
